import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GiftPreview } from "@/components/GiftPreview";
import { GiftsSent } from "@/components/GiftsSent";

const NOTE_MAX = 500;

export default function GiftPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ recipient_email: "", recipient_name: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value.slice(0, k === "note" ? NOTE_MAX : 200) });
  useEffect(() => { if (sp.get("cancelled")) toast("Checkout cancelled. Your gift wasn't sent."); }, [sp]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/checkout", { ...f, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (ex) {
      toast.error(formatApiError(ex));
      setBusy(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen px-6 md:px-12 py-10 max-w-6xl mx-auto" data-testid="gift-page">
      <Link to="/billing" className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 mb-10 w-fit" data-testid="gift-back-link"><ArrowLeft className="w-4 h-4" /> Back to billing</Link>
      <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-3">Gift Pro</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 mb-4">Give the gift of unlimited worlds</h1>
      <p className="text-slate-400 mb-12 max-w-xl">One year of Lorely Pro for $49.99. It starts the day they redeem it and never renews on its own.</p>
      <div className="grid lg:grid-cols-2 gap-10 items-start mb-12">
        <form onSubmit={submit} className="space-y-5" data-testid="gift-form">
          <div className="space-y-1.5"><Label>Their email</Label>
            <Input type="email" required value={f.recipient_email} onChange={set("recipient_email")} placeholder="coauthor@story.com" data-testid="gift-email-input" /></div>
          <div className="space-y-1.5"><Label>Their name <span className="text-slate-500">(optional)</span></Label>
            <Input value={f.recipient_name} onChange={set("recipient_name")} placeholder="Octavia" data-testid="gift-name-input" /></div>
          <div className="space-y-1.5">
            <Label>A personal note <span className="text-slate-500">(optional)</span></Label>
            <div className="rounded-xl bg-[#f6f0e1] p-4 shadow-inner rotate-[-0.4deg]">
              <textarea value={f.note} onChange={set("note")} rows={5} placeholder="For the saga we keep promising to finish..."
                className="w-full resize-none bg-transparent font-display italic text-lg text-[#2b2416] placeholder:text-[#a79c84] focus:outline-none" data-testid="gift-note-input" />
              <p className="text-right font-code text-[10px] text-[#8a8170]" data-testid="gift-note-count">{f.note.length}/{NOTE_MAX}</p>
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full h-11 bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="gift-pay-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Pay $49.99 and send gift
          </Button>
          <p className="text-[11px] text-slate-500">Secure one-time payment by Stripe. We email the gift as soon as payment is confirmed.</p>
        </form>
        <GiftPreview buyer={user.name || "A fellow writer"} recipient={f.recipient_name.trim()} note={f.note} />
      </div>
      <GiftsSent />
    </div>
  );
}
